import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { PropertyCard, useCompanyDateTime } from '@innovyze/stylovyze';
import { Delete, Edit, ContentCopy, Publish, Unpublished, CheckCircle, RemoveCircle } from '@mui/icons-material';
import { Link, Box, Tooltip } from '@mui/material';
import { formatDateTimeWithFormatDate } from '@innovyze/lib-am-common/Utils';

import { FailureDefinition } from '@Types';
import { useGlobalization } from '@Translations';
import { addRouteNamespace } from '@Utils/actions';
import { deleteFailureDefinition, copyFailureDefinition } from '@Actions/failureDefinition.actions';
import { DeleteFailureDefinitionDialog } from './DeleteFailureDefinitionDialog';

interface FailureDefinitionCardProps {
	failureDefinition: FailureDefinition;
	onPublishToggle: (failureDefinition: FailureDefinition, published: boolean) => void;
}

export const FailureDefinitionCard = ({ failureDefinition, onPublishToggle }: FailureDefinitionCardProps) => {
	const { t } = useGlobalization();
	const history = useHistory();
	const dispatch = useDispatch();
	const { formatDate } = useCompanyDateTime();
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [isCopying, setIsCopying] = useState(false);

	const handleDelete = () => {
		dispatch(deleteFailureDefinition({ id: failureDefinition.id }));
		setIsDeleteDialogOpen(false);
	};

	const handleCopy = () => {
		setIsCopying(true);
		dispatch(
			copyFailureDefinition({
				id: failureDefinition.id,
				onSuccess: (newId: string) => {
					setIsCopying(false);
					history.push(addRouteNamespace(`failure-definition/${newId}`));
				},
				onError: () => {
					setIsCopying(false);
				},
			}),
		);
	};

	const handlePublishToggle = () => {
		onPublishToggle(failureDefinition, !failureDefinition.published);
	};

	return (
		<>
			<DeleteFailureDefinitionDialog
				open={isDeleteDialogOpen}
				failureDefinitionName={failureDefinition.name}
				onClose={() => setIsDeleteDialogOpen(false)}
				onConfirm={handleDelete}
			/>
			<PropertyCard
				columns={2}
				forceMenu
				menuItems={[
					{
						text: t('Edit'),
						icon: <Edit />,
						onClick: () => {
							history.push(addRouteNamespace(`failure-definition/${failureDefinition.id}`));
						},
					},
					{
						text: t('Delete'),
						icon: <Delete />,
						onClick: () => {
							setIsDeleteDialogOpen(true);
						},
						props: {
							disabled: failureDefinition.published,
						},
					},
					{
						text: t('Copy'),
						icon: <ContentCopy />,
						onClick: () => {
							handleCopy();
						},
						props: {
							disabled: isCopying,
						},
					},
					{
						text: failureDefinition.published ? t('Unpublish') : t('Publish'),
						icon: failureDefinition.published ? <Unpublished /> : <Publish />,
						onClick: handlePublishToggle,
					},
				]}
				className="failure-definition-card"
				properties={[
					{
						title: t('Last Run'),
						value: `${
							failureDefinition.lastRunAt ? formatDateTimeWithFormatDate(failureDefinition.lastRunAt, formatDate) : '-'
						}`,
						type: 'text',
						ellipsis: true,
					},
					{
						title: t('Status'),
						type: 'status',
						value: failureDefinition.status,
						ellipsis: true,
					},
					{
						title: t('Run By'),
						value: `${failureDefinition.runBy || '-'}`,
						ellipsis: true,
					},
					{
						title: t('System and Asset Type'),
						value:
							failureDefinition.systemType && failureDefinition.assetType
								? `${failureDefinition.systemType} - ${failureDefinition.assetType}`
								: '-',
						ellipsis: true,
					},
					{
						title: t('Inspection Standard'),
						value: `${failureDefinition.inspectionStandard || '-'}`,
						ellipsis: true,
					},
					{
						title: t('Asset Count'),
						value: `${failureDefinition.assetCount || '-'}`,
						ellipsis: true,
					},
				]}
				title={
					<Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
						<Link
							underline="hover"
							style={{
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								cursor: 'pointer',
								flex: 1,
								whiteSpace: 'nowrap',
							}}
							title={failureDefinition.name}
							onClick={() => history.push(addRouteNamespace(`failure-definition/${failureDefinition.id}`))}>
							{failureDefinition.name}
						</Link>
						{failureDefinition.published ? (
							<Tooltip title={t('Published')} arrow>
								<CheckCircle sx={{ color: '#4caf50', fontSize: 20, ml: 1, flexShrink: 0 }} />
							</Tooltip>
						) : (
							<Tooltip title={t('Unpublished')} arrow>
								<RemoveCircle sx={{ color: '#9e9e9e', fontSize: 20, ml: 1, flexShrink: 0 }} />
							</Tooltip>
						)}
					</Box>
				}
			/>
		</>
	);
};
